import React, { useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(28, 25, 23, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-md);
`;

const Box = styled.div<{ $maxWidth?: string }>`
  background: var(--card-bg);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: ${({ $maxWidth = '32rem' }) => $maxWidth};
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 100vh;
    height: 100%;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
  border-bottom: 1px solid var(--gray-100);

  h3 {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-xs);
  border-radius: var(--border-radius-sm);
  color: var(--gray-500);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    background: var(--gray-100);
    color: var(--gray-800);
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const Body = styled.div`
  padding: var(--space-lg);
  overflow-y: auto;
  flex: 1;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
  border-top: 1px solid var(--gray-100);
`;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
  /** Se false, il click sull'overlay non chiude la modale. Default true. */
  closeOnOverlay?: boolean;
}

/**
 * Modale standard dell'applicazione.
 *   <Modal open={open} onClose={...} title="Titolo" footer={<Button .../>}>...</Modal>
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth,
  closeOnOverlay = true,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Overlay onMouseDown={closeOnOverlay ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}>
      <Box $maxWidth={maxWidth} role="dialog" aria-modal="true">
        {title !== undefined && (
          <Header>
            <h3>{title}</h3>
            <CloseButton onClick={onClose} aria-label="Chiudi">
              <X />
            </CloseButton>
          </Header>
        )}
        <Body>{children}</Body>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </Box>
    </Overlay>
  );
};

export default Modal;
